import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Ban, CheckCircle2, PackageCheck, Pencil, Plus, Receipt, Send, ShoppingCart, Trash2, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { usePoActionMutation, usePurchaseOrdersQuery, useUpsertPoMutation } from '@/client/api/business/spend';
import { PO_STATUS_BADGE, type PoLine, type PurchaseOrderV2 } from '@/client/types/business/spend';
import { BusinessPageShell } from '../components/BusinessPageShell';
import { Field, filterClass, inputClass, textareaClass, useSpendLookups } from '../../Accounting/components/spend-ui';
import { DeleteConfirmDialog, EmptyState, RowMenu, StatusBadge } from '../../HumanResource/components/hr-ui';
import { money, totals } from '../../HumanResource/components/comp-format';
import { formatDay } from '../../HumanResource/components/leave-format';

type Action = 'submit' | 'approve' | 'order' | 'receive' | 'cancel';
const ACTION_TEXT: Record<Action, { title: string; body: string; label: string }> = {
  submit: { title: 'Send for approval?', body: 'It can no longer be edited. Someone other than you approves it.', label: 'Send' },
  approve: { title: 'Approve this order?', body: 'It can then be placed with the vendor.', label: 'Approve' },
  order: { title: 'Mark as ordered?', body: 'Record that it has been placed with the vendor.', label: 'Mark ordered' },
  receive: { title: 'Mark as received?', body: 'A draft bill for the total is added to Bills, due on the vendor’s payment terms.', label: 'Mark received' },
  cancel: { title: 'Cancel this order?', body: 'It stays in the list as cancelled.', label: 'Cancel order' },
};

/** Orders to vendors, approved by someone other than the requester. */
export default function PurchasesPage() {
  const navigate = useNavigate();
  const q = usePurchaseOrdersQuery();
  const lookups = useSpendLookups();
  const upsert = useUpsertPoMutation();
  const act = usePoActionMutation();
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [status, setStatus] = useState<PurchaseOrderV2['status'] | 'open' | 'all'>('open');
  const [form, setForm] = useState<Partial<PurchaseOrderV2> | null>(null);
  const [pending, setPending] = useState<{ po: PurchaseOrderV2; action: Action } | null>(null);

  const orders = q.data ?? [];
  const isOpen = (o: PurchaseOrderV2) => !['received', 'cancelled'].includes(o.status);
  const shown = orders.filter((o) => (status === 'all' ? true : status === 'open' ? isOpen(o) : o.status === status));
  const lines = form?.lines ?? [];
  const setLine = (i: number, patch: Partial<PoLine>) => setForm({ ...form, lines: lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const formTotal = lines.reduce((n, l) => n + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0);

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    upsert.mutate(
      {
        id: form.id,
        vendorId: form.vendorId,
        currency: (form.currency || 'GHS').toUpperCase(),
        lines: lines.map((l) => ({ description: l.description, quantity: Number(l.quantity), unitPrice: Number(l.unitPrice) })),
        departmentId: form.departmentId || null,
        categoryId: form.categoryId || null,
        neededBy: form.neededBy || null,
        notes: form.notes,
      },
      {
        onSuccess: (po) => {
          toast.success(`${po.number} saved as a draft`);
          setForm(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save'),
      },
    );
  };

  return (
    <BusinessPageShell
      mock={false}
      title="Purchase orders"
      description="Draft → sent for approval → approved (by someone other than the requester) → ordered → received. Receiving adds a draft bill."
      crumbs={[{ label: 'Procurement' }, { label: 'Purchase orders' }]}
      icon={ShoppingCart}
      actions={
        <PermissionButton
          code={PERMISSIONS.accounting.manage}
          className="gap-2"
          onClick={() => setForm({ currency: 'GHS', lines: [{ description: '', quantity: 1, unitPrice: 0 }], notes: '' })}
        >
          <Plus className="h-4 w-4" /> New order
        </PermissionButton>
      }
      stats={[
        { label: 'Waiting for approval', value: String(orders.filter((o) => o.status === 'submitted').length) },
        { label: 'Approved, not ordered', value: String(orders.filter((o) => o.status === 'approved').length) },
        { label: 'On order', value: totals(orders.filter((o) => o.status === 'ordered').map((o) => ({ amount: o.total, currency: o.currency }))) },
      ]}
    >
      <div className="mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className={filterClass}>
          <option value="open">Open</option>
          <option value="all">All</option>
          {Object.keys(PO_STATUS_BADGE).map((s) => (
            <option key={s} value={s} className="capitalize">
              {s}
            </option>
          ))}
        </select>
      </div>
      {q.isError ? (
        <EmptyState label={`Could not load orders: ${(q.error as Error)?.message ?? 'unknown error'}`} />
      ) : q.isLoading ? (
        <EmptyState label="Loading orders…" />
      ) : shown.length === 0 ? (
        <EmptyState label="No orders here." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium">Vendor</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Needed by</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((o) => (
                <tr key={o.id} className={canManage && o.status === 'draft' ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && o.status === 'draft' && setForm(o)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{o.number}</p>
                    <p className="max-w-xs truncate text-xs text-gray-400">{o.lines.map((l) => l.description).join(', ')}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{lookups.vendorName(o.vendorId)}</td>
                  <td className="px-4 py-3 text-gray-900">{money(o.total, o.currency)}</td>
                  <td className="px-4 py-3 text-gray-600">{o.neededBy ? formatDay(o.neededBy) : '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={o.status} className={`capitalize ${PO_STATUS_BADGE[o.status]}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: 'Edit', icon: Pencil, hidden: !canManage || o.status !== 'draft', onSelect: () => setForm(o) },
                        { label: 'Send for approval', icon: Send, hidden: !canManage || o.status !== 'draft', onSelect: () => setPending({ po: o, action: 'submit' }) },
                        { label: 'Approve', icon: CheckCircle2, hidden: !canManage || o.status !== 'submitted', onSelect: () => setPending({ po: o, action: 'approve' }) },
                        { label: 'Mark ordered', icon: Truck, hidden: !canManage || o.status !== 'approved', onSelect: () => setPending({ po: o, action: 'order' }) },
                        { label: 'Mark received', icon: PackageCheck, hidden: !canManage || !['approved', 'ordered'].includes(o.status), onSelect: () => setPending({ po: o, action: 'receive' }) },
                        { label: 'Open bill', icon: Receipt, hidden: !o.billId, onSelect: () => navigate(`/admin/accounting/payables/bills?vendor=${o.vendorId}`) },
                        { label: 'Cancel', icon: Ban, danger: true, hidden: !canManage || !isOpen(o), onSelect: () => setPending({ po: o, action: 'cancel' }) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form?.number ? `Edit ${form.number}` : 'New purchase order'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Vendor">
                  <select required value={form.vendorId ?? ''} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} className={inputClass}>
                    <option value="">Select</option>
                    {lookups.vendors.filter((v) => v.status === 'active').map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Currency">
                  <input maxLength={3} value={form.currency ?? 'GHS'} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Needed by">
                  <input type="date" value={form.neededBy ?? ''} onChange={(e) => setForm({ ...form, neededBy: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Department">
                  <select value={form.departmentId ?? ''} onChange={(e) => setForm({ ...form, departmentId: e.target.value })} className={inputClass}>
                    <option value="">Company-wide</option>
                    {lookups.departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Category">
                  <select value={form.categoryId ?? ''} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className={inputClass}>
                    <option value="">None</option>
                    {lookups.categories.filter((c) => c.status === 'active').map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div>
                <p className="mb-1.5 text-xs font-medium text-gray-600">Items</p>
                <div className="space-y-2">
                  {lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <input className={`${inputClass} col-span-6`} placeholder="What" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} required />
                      <input className={`${inputClass} col-span-2`} type="number" min={0} step="any" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} />
                      <input className={`${inputClass} col-span-3`} type="number" min={0} step="0.01" placeholder="Unit price" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: Number(e.target.value) })} />
                      <button type="button" className="col-span-1 text-gray-400 hover:text-red-600" aria-label="Remove line" onClick={() => setForm({ ...form, lines: lines.filter((_, j) => j !== i) })}>
                        <Trash2 className="mx-auto h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <Button type="button" size="sm" variant="outline" className="gap-1" onClick={() => setForm({ ...form, lines: [...lines, { description: '', quantity: 1, unitPrice: 0 }] })}>
                    <Plus className="h-3.5 w-3.5" /> Add line
                  </Button>
                  <span className="text-sm font-medium text-gray-900">Total {money(formTotal, (form.currency || 'GHS').toUpperCase())}</span>
                </div>
              </div>
              <Field label="Notes">
                <textarea rows={2} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={textareaClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  Save draft
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending ? `${pending.po.number}: ${ACTION_TEXT[pending.action].title}` : ''}
        description={pending ? `${lookups.vendorName(pending.po.vendorId)} · ${money(pending.po.total, pending.po.currency)}. ${ACTION_TEXT[pending.action].body}` : ''}
        actionLabel={pending ? ACTION_TEXT[pending.action].label : ''}
        danger={pending?.action === 'cancel'}
        onConfirm={() => {
          if (pending)
            act.mutate(
              { id: pending.po.id, action: pending.action },
              { onSuccess: () => toast.success(pending.action === 'receive' ? 'Received; draft bill added' : 'Done'), onError: (e) => toast.error((e as Error).message) },
            );
          setPending(null);
        }}
      />
    </BusinessPageShell>
  );
}
